import React, {useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {Badge, Button, FilePicker, Input, Modal} from '@/components';
import {Select} from '@/components/common/Input';
import {fileService, kycBucketKey} from '@/services/fileService';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {formatBusinessAddress} from '@/utils/merchant';

import type {KYBModalFormProps, KYBModalProps, ProfileType} from './types';

const TEXT_FIELDS: (keyof ProfileType)[] = [
  'BUSINESS_TYPE',
  'BUSINESS_NATURE',
  'BUSINESS_NUMBER',
  'BUSINESS_WEBSITE',
  'BUSINESS_ADDRESS',
];

const FILE_FIELDS = [
  'PROOF_OF_BUSINESS_ADDRESS',
  'ARTICLES_DOCUMENT',
  'SHAREHOLDER_REGISTRY',
  'BUSINESS_CERTIFICATE',
];

const DOCUMENT_TYPES = {
  // Text fields
  BUSINESS_TYPE: 'Business Type',
  BUSINESS_NATURE: 'Business Nature',
  BUSINESS_NUMBER: 'Business Number',
  BUSINESS_WEBSITE: 'Business Website',
  BUSINESS_ADDRESS: 'Business Address',
  // File fields
  PROOF_OF_BUSINESS_ADDRESS: 'Proof of Business Address',
  ARTICLES_DOCUMENT: 'Articles Document',
  SHAREHOLDER_REGISTRY: 'Shareholder Registry',
  BUSINESS_CERTIFICATE: 'Business Certificate',
};

// Module-private: nothing outside this file consumes it, and exporting a
// non-component alongside the modal breaks the file's Fast Refresh boundary.
const businessEntityOptions = [
  {
    key: 'Individual (Sole Proprietor / Trader)',
    value: 'Individual (Sole Proprietor / Trader)',
  },
  {
    key: 'Partnership (General / Limited / LLP)',
    value: 'Partnership (General / Limited / LLP)',
  },
  {
    key: 'Company (Ltd / LLC / C-Corp / Pvt Ltd / PLC)',
    value: 'Company (Ltd / LLC / C-Corp / Pvt Ltd / PLC)',
  },
  {key: 'NGO / Non-Profit', value: 'NGO / Non-Profit'},
  {key: 'Cooperative', value: 'Cooperative'},
  {
    key: 'Governmental or Public Sector',
    value: 'Governmental or Public Sector',
  },
];

const KYBProfileForm: React.FC<KYBModalFormProps> = ({
  onSubmit,
  onClose,
  isKycSubmitting,
  business,
}) => {
  const initialBusinessAddress = formatBusinessAddress(
    business?.meta?.kyb?.data?.BUSINESS_ADDRESS,
  );

  const [profile, setProfile] = useState<ProfileType>({
    BUSINESS_TYPE: business?.meta?.kyb?.data?.BUSINESS_TYPE || '',
    BUSINESS_NATURE: business?.meta?.kyb?.data?.BUSINESS_NATURE || '',
    BUSINESS_NUMBER: business?.meta?.kyb?.data?.BUSINESS_NUMBER || '',
    BUSINESS_WEBSITE: business?.meta?.kyb?.data?.BUSINESS_WEBSITE || '',
    BUSINESS_ADDRESS: initialBusinessAddress || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    return onSubmit({
      ...profile,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="p-2">
          {TEXT_FIELDS.map((field) => {
            if (field === 'BUSINESS_TYPE') {
              return (
                <div key={field} className="mb-3">
                  <Select
                    id={field}
                    label={DOCUMENT_TYPES[field as keyof typeof DOCUMENT_TYPES]}
                    value={profile[field] || ''}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setProfile((prev) => ({
                        ...prev,
                        [field]: e.target.value,
                      }))
                    }
                    className="w-full"
                    placeholder={`Select Business Type`}
                    options={businessEntityOptions}
                  />
                </div>
              );
            } else {
              return (
                <div key={field} className="mb-3">
                  <Input
                    id={field}
                    label={DOCUMENT_TYPES[field as keyof typeof DOCUMENT_TYPES]}
                    value={profile[field] || ''}
                    type={'text'}
                    onChange={(e) =>
                      setProfile((prev) => ({
                        ...prev,
                        [field]: e.target.value,
                      }))
                    }
                    className="w-full"
                    placeholder={`Enter ${DOCUMENT_TYPES[field as keyof typeof DOCUMENT_TYPES].toLowerCase()}`}
                  />
                </div>
              );
            }
          })}
        </div>
        <div className="flex w-full justify-end space-x-2 py-5">
          <Button colorScheme="cyan" onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            disabled={isKycSubmitting}
            type="submit"
            variant="solid"
            colorScheme="cyan"
          >
            Update
          </Button>
        </div>
      </form>
    </>
  );
};

const KYBDocumentsForm: React.FC<KYBModalFormProps> = ({
  onSubmit,
  isKycSubmitting,
  onClose,
  businessId,
}) => {
  const [documents, setDocuments] = useState<Record<string, string>>({});
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  // Derived straight from `documents` over the module-level FILE_FIELDS list,
  // so there is nothing for an effect to synchronize.
  const uploadedDocs = useMemo(
    () => FILE_FIELDS.filter((file) => !!documents[file]),
    [documents],
  );

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedDocType) {
      return;
    }

    setUploadingDoc(selectedDocType);
    try {
      const {putUrl} = await fileService.uploadFile(
        file.name,
        kycBucketKey,
        businessId,
      );
      if (!putUrl) {
        throw new Error('Failed to fetch upload link. Retry');
      }
      const response = await fileService.uploadToS3(putUrl, file);
      if (response.status !== 200) {
        throw new Error(`Upload of ${selectedDocType} to s3 failed`);
      }
      setDocuments((prev) => ({...prev, [selectedDocType]: file.name}));
      toast.success(
        `${DOCUMENT_TYPES[selectedDocType as keyof typeof DOCUMENT_TYPES]} uploaded successfully`,
      );
    } catch (error) {
      toast.error(getErrorMessage(error, ''));
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // `onSubmit` (useMerchantKYB.updateBusinessKYB) toasts its own failures, so
    // this only fires if that handler itself throws. Log rather than swallow.
    onSubmit({
      ...documents,
    }).catch(logRejection('Failed to submit KYB documents'));
  };
  return (
    <form onSubmit={handleSubmit}>
      <div className="p-2">
        <div className="space-y-4">
          <div>
            <Select
              label="Select Document To Upload"
              placeholder="Choose a document..."
              options={FILE_FIELDS.map((field) => ({
                key: DOCUMENT_TYPES[field as keyof typeof DOCUMENT_TYPES],
                value: field,
              }))}
              value={selectedDocType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedDocType(e.target.value)
              }
            />
          </div>

          {selectedDocType && (
            <div>
              {uploadingDoc === selectedDocType ? (
                <div className="flex justify-center items-center border border-border rounded-lg py-8 bg-muted">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : (
                <FilePicker
                  isLoading={false}
                  label={
                    DOCUMENT_TYPES[
                      selectedDocType as keyof typeof DOCUMENT_TYPES
                    ]
                  }
                  name={selectedDocType}
                  onChange={handleFileUpload}
                  className="mt-0 dark:!bg-transparent"
                  accept={'.pdf'}
                />
              )}
              {uploadedDocs?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {uploadedDocs.map((doc) => (
                    <Badge className="text-xs" colorScheme="blue" key={doc}>
                      {doc}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex w-full justify-end space-x-2 py-5">
        <Button colorScheme="cyan" onClick={onClose} variant="outline">
          Cancel
        </Button>
        <Button
          disabled={
            isKycSubmitting ||
            uploadingDoc === selectedDocType ||
            typeof documents[selectedDocType || ''] === 'undefined'
          }
          type="submit"
          variant="solid"
          colorScheme="cyan"
        >
          Upload
        </Button>
      </div>
    </form>
  );
};

const BusinessKYBModal: React.FC<KYBModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isKycSubmitting,
  type = 'Profile',
  business,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      closeOnOutsideClick
      title={`${type === 'Profile' ? 'Update' : 'Upload'} KYB Details`}
    >
      {type === 'Profile' ? (
        <KYBProfileForm
          isKycSubmitting={isKycSubmitting}
          onSubmit={onSubmit}
          onClose={onClose}
          business={business}
        />
      ) : (
        <KYBDocumentsForm
          isKycSubmitting={isKycSubmitting}
          onSubmit={onSubmit}
          onClose={onClose}
          businessId={business?.id || ''}
        />
      )}
    </Modal>
  );
};

export default BusinessKYBModal;
